import { UNDECIDED } from "@/components/projects/diagnostic/data/wricefDecisions";
import type { components } from "@/types/api.generated";
import { parseContentDispositionFilename } from "./artifacts";
import apiClient from "./client";
import type {
  WricefByPackageResponse,
  WricefCatalogItem,
  WricefCatalogQuery,
  WricefCatalogResponse,
  WricefCatalogSummary,
  WricefComponent,
  WricefComponentsResponse,
  WricefWorklistQuery,
  WricefWorklistResponse,
} from "@/types/wricef";

/**
 * O "sem decisão" na API: o valor do filtro `decision` da F1b (#1500). Na tela
 * ele é o `UNDECIDED`, o balde do card do Ato 1. A tradução entre os dois mora
 * só aqui: no parâmetro que sai e na contagem que volta.
 */
const API_UNDECIDED = "undecided";

export function buildParams(query: WricefCatalogQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (query.wricef?.length) {
    for (const w of query.wricef) params.append("wricef", w);
  }
  if (query.module?.length) {
    for (const m of query.module) params.append("module", m);
  }
  if (query.stream?.length) {
    for (const st of query.stream) params.append("stream", st);
  }
  if (query.min_criticality !== undefined) {
    params.set("min_criticality", String(query.min_criticality));
  }
  if (query.max_criticality !== undefined) {
    params.set("max_criticality", String(query.max_criticality));
  }
  // Um parâmetro por valor: a api lê `Query(List[str])`, que espera repetição.
  // Juntar com vírgula produziria um valor único que não casa com estado nenhum.
  if (query.object_state?.length) {
    for (const s of query.object_state) params.append("object_state", s);
  }
  if (query.unit_role?.length) {
    for (const r of query.unit_role) params.append("unit_role", r);
  }
  if (query.without_executable) params.set("without_executable", "true");
  if (query.decision?.length) {
    for (const d of query.decision) {
      params.append("decision", d === UNDECIDED ? API_UNDECIDED : d);
    }
  }
  if (query.out_of_scope_without_reason) params.set("out_of_scope_without_reason", "true");
  if (query.source) params.set("source", query.source);
  if (query.scan_job_id) params.set("scan_job_id", query.scan_job_id);
  if (query.order_by) params.set("order_by", query.order_by);
  if (query.order_desc !== undefined) params.set("order_desc", String(query.order_desc));
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  return params;
}

export async function getWricefCatalog(
  projectId: string,
  query: WricefCatalogQuery = {},
): Promise<WricefCatalogResponse> {
  const params = buildParams(query);
  const qs = params.toString();
  const url = `/api/projects/${projectId}/wricef-catalog${qs ? `?${qs}` : ""}`;
  const res = await apiClient.get<WricefCatalogResponse>(url);
  return withScreenUndecided(res.data);
}

/** A contagem por decisão com o `UNDECIDED` da tela no lugar do `undecided` da API. */
function withScreenUndecided(page: WricefCatalogResponse): WricefCatalogResponse {
  if (!page.decision_counts) return page;
  const { [API_UNDECIDED]: undecided, ...decided } = page.decision_counts;
  return { ...page, decision_counts: { ...decided, [UNDECIDED]: undecided ?? 0 } };
}

/** Teto de `limit` do endpoint da lista (`le=1000`). */
const MAX_PAGE = 1000;

/**
 * Todas as linhas do filtro, página a página. É o que o CSV exporta: a tela mostra
 * uma página, e exportar só ela entregaria 50 linhas de um catálogo de 7.288.
 */
export async function fetchAllWricefCatalog(
  projectId: string,
  query: Omit<WricefCatalogQuery, "limit" | "offset">,
): Promise<WricefCatalogItem[]> {
  const items: WricefCatalogItem[] = [];
  let total = Infinity;
  while (items.length < total) {
    const page = await getWricefCatalog(projectId, {
      ...query,
      limit: MAX_PAGE,
      offset: items.length,
    });
    total = page.total;
    items.push(...page.items);
    // Catálogo que encolheu entre duas páginas: sem esta guarda o laço não termina.
    if (page.items.length === 0) break;
  }
  return items;
}

export async function getWricefWorklist(
  projectId: string,
  query: WricefWorklistQuery = {},
): Promise<WricefWorklistResponse> {
  const params = new URLSearchParams();
  for (const st of query.stream ?? []) params.append("stream", st);
  if (query.scan_job_id) params.set("scan_job_id", query.scan_job_id);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  if (query.offset !== undefined) params.set("offset", String(query.offset));
  const qs = params.toString();
  const res = await apiClient.get<WricefWorklistResponse>(
    `/api/projects/${projectId}/wricef-catalog/worklist${qs ? `?${qs}` : ""}`,
  );
  return res.data;
}

export async function getWricefCatalogSummary(
  projectId: string,
  scanJobId?: string | null,
): Promise<WricefCatalogSummary> {
  const qs = scanJobId ? `?scan_job_id=${encodeURIComponent(scanJobId)}` : "";
  const res = await apiClient.get<WricefCatalogSummary>(
    `/api/projects/${projectId}/wricef-catalog/summary${qs}`,
  );
  return res.data;
}

export async function getWricefByPackage(
  projectId: string,
  scanJobId?: string | null,
): Promise<WricefByPackageResponse> {
  const qs = scanJobId ? `?scan_job_id=${encodeURIComponent(scanJobId)}` : "";
  const res = await apiClient.get<WricefByPackageResponse>(
    `/api/projects/${projectId}/wricef-catalog/by-package${qs}`,
  );
  return res.data;
}

export async function getWricefObject(
  projectId: string,
  objName: string,
): Promise<WricefCatalogItem> {
  const res = await apiClient.get<WricefCatalogItem>(
    `/api/projects/${projectId}/wricef-catalog/${encodeURIComponent(objName)}`,
  );
  return res.data;
}

/** As peças do executável, em árvore (#1083). */
export async function getWricefComponents(
  projectId: string,
  objName: string,
): Promise<WricefComponent[]> {
  const res = await apiClient.get<WricefComponentsResponse>(
    `/api/projects/${projectId}/wricef-catalog/${encodeURIComponent(objName)}/components`,
  );
  return res.data.components;
}

/** Download the extracted .abap source for one catalog object from MinIO. */
export async function downloadWricefSource(
  projectId: string,
  objName: string,
): Promise<void> {
  const res = await apiClient.get<Blob>(
    `/api/projects/${projectId}/wricef-catalog/${encodeURIComponent(objName)}/source`,
    { responseType: "blob" },
  );
  const blob = new Blob([res.data], { type: "text/x-abap" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${objName}.abap`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** Open the .abap source in a new browser tab as plain text (#104 critério 4).
 *
 * The API endpoint serves with ``Content-Disposition: attachment`` for the
 * "Baixar .abap" flow. To open inline we fetch with auth headers (Bearer),
 * build a blob URL with ``text/plain`` so the browser renders it instead of
 * downloading, and ``window.open`` the result. The blob URL is revoked on
 * the next tick — long enough for the new tab to load, short enough to free
 * memory.
 */
export async function openWricefSourceInTab(
  projectId: string,
  objName: string,
): Promise<void> {
  const res = await apiClient.get<Blob>(
    `/api/projects/${projectId}/wricef-catalog/${encodeURIComponent(objName)}/source`,
    { responseType: "blob" },
  );
  const blob = new Blob([res.data], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, "_blank", "noopener,noreferrer");
  if (!opened) {
    URL.revokeObjectURL(url);
    throw new Error("Popup bloqueado pelo navegador");
  }
  // Give the new tab time to fetch the blob before we revoke it.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export type WricefClientReportPreview = components["schemas"]["ClientReportPreview"];
export type WricefClientReportScope = WricefClientReportPreview["scope"];

/** O que a janela do relatório para o cliente escolhe (#1620, EF-ARCH-002). */
export interface WricefClientReportOptions {
  scope: WricefClientReportScope;
  includeClientColumns: boolean;
  /** Tira do arquivo o objeto que a IA ainda não analisou (cartão 3.5). */
  analyzedOnly: boolean;
}

/**
 * Os filtros da lista e as opções da janela. A página fica de fora: o
 * relatório é o filtro inteiro, e o servidor aplica o escopo sobre ele.
 */
function clientReportParams(
  query: WricefCatalogQuery,
  options: WricefClientReportOptions,
): string {
  const params = buildParams({ ...query, limit: undefined, offset: undefined });
  params.set("scope", options.scope);
  params.set("include_client_columns", String(options.includeClientColumns));
  params.set("analyzed_only", String(options.analyzedOnly));
  return params.toString();
}

/** As contagens, as primeiras linhas e o nome do arquivo, antes de gerar. */
export async function getWricefClientReportPreview(
  projectId: string,
  query: WricefCatalogQuery,
  options: WricefClientReportOptions,
): Promise<WricefClientReportPreview> {
  const res = await apiClient.get<WricefClientReportPreview>(
    `/api/projects/${projectId}/wricef-catalog/client-report/preview?${clientReportParams(query, options)}`,
  );
  return res.data;
}

/** Gera o .xlsx no servidor e baixa com o nome que ele deu. */
export async function downloadWricefClientReport(
  projectId: string,
  query: WricefCatalogQuery,
  options: WricefClientReportOptions,
): Promise<void> {
  const res = await apiClient.get<Blob>(
    `/api/projects/${projectId}/wricef-catalog/client-report?${clientReportParams(query, options)}`,
    { responseType: "blob", timeout: 120_000 },
  );
  const name =
    parseContentDispositionFilename(res.headers?.["content-disposition"]) ??
    "myGROW_WRICEF_Validacao_Cliente.xlsx";
  const url = URL.createObjectURL(new Blob([res.data]));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/** Download the whole WRICEF repository for a project as a ZIP. */
export async function downloadWricefRepository(projectId: string): Promise<void> {
  const res = await apiClient.get<Blob>(
    `/api/projects/${projectId}/wricef-catalog/download`,
    { responseType: "blob" },
  );
  const blob = new Blob([res.data], { type: "application/zip" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `wricef-${projectId}.zip`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
